import React from 'react';
import classNames from 'classnames';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import ProTabBarToasterContainer from './ProTabToasters/ProTabBarToasterContainer';
import NotificationsBadge from '../notifications/NotificationsBadge';
import { NavBarItem, IconRenderer } from '../../shared/BottomNavBar';
import {
  BasicHomeIcon,
  TimeCalendarIcon,
  BasicListIcon,
  BasicBarChartIcon,
  BasicMenuIcon,
  MegaPhoneIcon,
} from '../../ui/Icons';

import './ProTabBar.scss';

import { MarketingToolTipContainer } from '../MarketingHub/Components/MarketingToolTip';
import OpenSupportPageButton from '../../shared/OpenSupportPageButton/OpenSupportPageButton';
import { Z_FG } from '../../ui/utilities/zIndex';

export type ProNavBarProps = {
  providerId: string | number;
  selectedTab: 'daily' | 'settings' | 'clients' | 'growth' | 'schedule' | 'marketing';
  showClientTabBadge: boolean;
  onGoToGrowth: () => void;
  onGoToSchedule: () => void;
  onGoToToday: () => void;
  onGoToSettings: () => void;
  onGoToClients: () => void;
  onGoToMarketing: () => void;
};

const renderIcon: IconRenderer = ({ selected }, Icon) => (
  <Box
    style={{ height: 20, width: 20 }}
    justifyContent="center"
    alignItems="center"
  >
    <Icon color={selected ? colors.punch : colors.ash} />
  </Box>
);

/**
 * Provides the pro navbar that appears on footer of mobile pro pages
 */
export const ProNavBar = ({
  /** Provider ID of logged in user */
  providerId,
  // proTab from stateParams takes precedence
  selectedTab,
  // show the badge on the client tab
  showClientTabBadge,
  onGoToGrowth,
  onGoToSchedule,
  onGoToToday,
  onGoToSettings,
  onGoToClients,
  onGoToMarketing,
}: ProNavBarProps) => (
  <React.Fragment>
    <Inline
      styleName="bottomNavBar"
      alignItems="center"
      justifyContent="space-around"
      color={colors.whiteWash}
      borderStyle="solid"
      borderTopWidth={1}
      borderTopColor={colors.grey3}
      flex={1}
      testID="pro-bottom-nav-bar"
      zIndex={Z_FG}
    >
      <Box
        className="pro-tab-bar-toaster-container"
      >
        <ProTabBarToasterContainer
          providerId={providerId}
        />
      </Box>
      <NavBarItem
        id="daily"
        label="Home"
        onSelect={onGoToToday}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
        selected={selectedTab === 'daily'}
        icon={BasicHomeIcon}
        badgeAppearance="client"
      >
        <NotificationsBadge
          className={classNames('bounce-in', 'pro-nav-notification-badge')}
          providerId={Number(providerId)}
          hide={selectedTab === 'daily'}
        />
      </NavBarItem>
      <NavBarItem
        id="schedule"
        label="Calendar"
        onSelect={onGoToSchedule}
        selected={selectedTab === 'schedule'}
        icon={TimeCalendarIcon}
        defaultColor={colors.ash}
        renderIcon={renderIcon}
      />
      <NavBarItem
        id="clients"
        label="Clients"
        onSelect={onGoToClients}
        selected={selectedTab === 'clients'}
        showBadge={showClientTabBadge}
        icon={BasicListIcon}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
      />
      <MarketingToolTipContainer
        providerId={providerId}
      >
        <NavBarItem
          id="marketing"
          label="Marketing"
          onSelect={onGoToMarketing}
          selected={selectedTab === 'marketing'}
          icon={MegaPhoneIcon}
          renderIcon={renderIcon}
          defaultColor={colors.ash}
        />
      </MarketingToolTipContainer>
      <NavBarItem
        id="growth"
        label="Earnings"
        onSelect={onGoToGrowth}
        selected={selectedTab === 'growth'}
        icon={BasicBarChartIcon}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
      />
      <NavBarItem
        id="settings"
        label="Menu"
        onSelect={onGoToSettings}
        selected={selectedTab === 'settings'}
        icon={BasicMenuIcon}
        renderIcon={renderIcon}
        defaultColor={colors.ash}
      />
    </Inline>
    <OpenSupportPageButton />
  </React.Fragment>
);

export default ProNavBar;
